import * as React from 'react';
import { useApiListener } from '@process-advisor/process-miner/shared/hooks/useApiListener';
import { Event } from '@process-advisor/process-miner/api/events';
import styled, { keyframes } from 'styled-components';
import Backdrop from '@mui/material/Backdrop';
import Dialog from '@mui/material/Dialog';
import useState from 'react-usestateref';
import { getColor } from '@process-advisor/process-miner/shared/theme';

const Card = styled.div`
    width: 460px;
    min-height: 230px;
    max-height: 270px;
    box-sizing: border-box;
    padding: 26px 30px 24px 30px;
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 18px 48px rgba(9, 30, 66, 0.28);
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const Eyebrow = styled.div`
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #5e6c84;
    margin-bottom: 8px;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    min-height: 58px;
`;

const Title = styled.div`
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 20px;
    line-height: 1.25;
    font-weight: 800;
    color: #172b4d;
    max-width: 310px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const Percent = styled.div`
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 30px;
    line-height: 1;
    font-weight: 850;
    color: ${getColor('blue')};
    white-space: nowrap;
`;

const BarTrack = styled.div`
    width: 100%;
    height: 12px;
    border-radius: 999px;
    background: #e9f2ff;
    margin-top: 22px;
    overflow: hidden;
`;

const BarFill = styled.div<{ width: number }>`
    height: 100%;
    width: ${({ width }) => `${width}%`};
    border-radius: 999px;
    background: ${getColor('blue')};
    transition: width 180ms ease;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 22px;
`;

const DetailBox = styled.div`
    border: 1px solid #dfe7f3;
    border-radius: 10px;
    padding: 12px 14px;
    background: #f8fbff;
    min-height: 58px;
    box-sizing: border-box;
`;

const DetailLabel = styled.div`
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 11px;
    line-height: 1.1;
    font-weight: 800;
    color: #6b778c;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 6px;
`;

const DetailValue = styled.div`
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 17px;
    line-height: 1.2;
    font-weight: 800;
    color: #172b4d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;


const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const pulse = keyframes`
    0%, 80%, 100% { opacity: 0.35; transform: scale(0.8); }
    40% { opacity: 1; transform: scale(1); }
`;

const GenericActivity = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 26px 0 18px 0;
    padding: 16px 18px;
    border: 1px solid #dfe7f3;
    border-radius: 12px;
    background: #f8fbff;
`;

const AiActivity = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 26px 0 18px 0;
    padding: 16px 18px;
    border: 1px solid #dfe7f3;
    border-radius: 12px;
    background: #f8fbff;
`;

const AiSpinner = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 4px solid #dfe7f3;
    border-top-color: ${getColor('blue')};
    animation: ${spin} 0.85s linear infinite;
    flex: 0 0 auto;
`;

const AiText = styled.div`
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 13px;
    line-height: 1.35;
    color: #44546f;
`;

const AiDots = styled.span`
    display: inline-flex;
    gap: 3px;
    margin-left: 4px;
    vertical-align: middle;

    span {
        width: 5px;
        height: 5px;
        border-radius: 999px;
        background: ${getColor('blue')};
        animation: ${pulse} 1.15s ease-in-out infinite;
    }

    span:nth-child(2) { animation-delay: 0.16s; }
    span:nth-child(3) { animation-delay: 0.32s; }
`;

const Footer = styled.div`
    margin-top: auto;
    padding-top: 16px;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 12px;
    line-height: 1.35;
    color: #5e6c84;
    min-height: 18px;
`;

type ParsedProgress = {
    mode: 'ai' | 'extraction' | 'generic';
    phase: string;
    percent: number;
    scanned: string;
    withHistory: string;
    detail: string;
    runId?: string;
    seq?: number;
};

function clampPercent(value: number): number {
    if (!Number.isFinite(value)) return 8;
    return Math.max(0, Math.min(100, Math.round(value)));
}

function cleanPhase(value: string): string {
    return String(value || 'Processing model')
        .replace(/\s+/g, ' ')
        .replace(/\s*[—-]\s*$/, '')
        .trim() || 'Processing model';
}



function getAiActivityMessage(elapsedMs: number): string {
    if (elapsedMs < 2500) return 'Sending prompt';
    if (elapsedMs < 90000) return 'Generating insights';
    return 'Still generating insights';
}

function isAiProgressStatus(status: unknown): boolean {
    const text = String(status || '').toLowerCase();
    return /\b(ai|generat\w*|analys\w*|assessment|advisor report|automation|opportunit\w*|healthcheck|framework|execution strategy|trend)\b/i.test(text)
        && !/\b(processing event log|records scanned|with history|sla checked|audit history|extracting|retrieving workflows|retrieving status codes|retrieving work types)\b/i.test(text);
}

const PROGRESS_PREFIX = '__PA_PROGRESS__';

function parseProgressStatus(status: unknown): ParsedProgress {
    const raw = String(status || '').replace(/\s+/g, ' ').trim();

    if (raw.startsWith(PROGRESS_PREFIX)) {
        try {
            const parsed = JSON.parse(raw.slice(PROGRESS_PREFIX.length));
            return {
                mode: parsed.mode || (parsed.ai === true ? 'ai' : 'extraction'),
                phase: cleanPhase(parsed.phase || 'Processing model'),
                percent: clampPercent(parsed.percent),
                scanned: parsed.primaryValue || '—',
                withHistory: parsed.secondaryValue || '—',
                detail: parsed.detail || parsed.note || 'Processing…',
                runId: parsed.runId || '',
                seq: Number(parsed.seq || 0),
            };
        } catch (err) {
            // Fall through to legacy parsing.
        }
    }

    if (!raw) {
        return {
            mode: 'generic',
            phase: 'Processing model',
            percent: 0,
            scanned: '—',
            withHistory: '—',
            detail: 'Preparing…',
        };
    }

    const aiMode = isAiProgressStatus(raw);
    const percentMatch = raw.match(/(?:^|[—\s])([0-9]{1,3})%/);
    const percent = clampPercent(percentMatch ? Number(percentMatch[1]) : 0);

    const scannedMatch = raw.match(/([0-9,]+)\s*\/\s*([0-9,]+)\s*scanned/i);
    const historyMatch = raw.match(/([0-9,]+)\s+with history/i);
    const extractionMode = Boolean(scannedMatch || historyMatch || /\b(audit history|sla checked|extracting|processing sla metrics|resolving labels|saving model data|model processing complete)\b/i.test(raw));

    let phase = raw;
    if (raw.includes('—')) {
        phase = raw.split('—')[0];
    } else if (percentMatch && percentMatch.index !== undefined) {
        phase = raw.substring(0, percentMatch.index);
    }

    phase = cleanPhase(phase);

    const scanned = scannedMatch ? `${scannedMatch[1]} / ${scannedMatch[2]}` : '—';
    const withHistory = historyMatch ? historyMatch[1] : '—';

    let detail = raw;
    if (raw.includes('•')) {
        detail = raw.split('•').slice(1).join(' • ').trim();
    } else if (raw.includes('—')) {
        detail = raw.split('—').slice(1).join('—').trim();
    }

    detail = detail
        .replace(/^[0-9]{1,3}%\s*[•-]?\s*/i, '')
        .trim();

    return {
        mode: aiMode ? 'ai' : (extractionMode ? 'extraction' : 'generic'),
        phase,
        percent,
        scanned,
        withHistory,
        detail: detail || (aiMode ? 'Generating…' : 'Working…'),
    };
}

export const ProgressModal = () => {
    const [isOpen, setIsOpen, open] = useState(false);
    const [status, setStatus, statusRef] = useState('');
    const lastProgressRef = React.useRef<{ runId: string; seq: number }>({ runId: '', seq: 0 });
    const [ai, setAI] = useState(false);
    const [aiStartedAt, setAiStartedAt] = useState<number | null>(null);
    const [aiElapsedMs, setAiElapsedMs] = useState(0);

    useApiListener(
        Event.openProgressModal,
        ({ status, ai }) => {
            const nextStatus = status || 'Preparing model processing…';
            const nextIsAi = Boolean(ai) || isAiProgressStatus(nextStatus);
            const parsedNext = parseProgressStatus(nextStatus);
            lastProgressRef.current = { runId: parsedNext.runId || '', seq: Number(parsedNext.seq || 0) };
            setIsOpen(true);
            setStatus(nextStatus);
            setAI(nextIsAi);
            if (nextIsAi) {
                setAiStartedAt(Date.now());
                setAiElapsedMs(0);
            } else {
                setAiStartedAt(null);
                setAiElapsedMs(0);
            }
        }
    );

    useApiListener(
        Event.updateProgressModal,
        ({ status, ai }) => {
            const nextStatus = status || 'Processing model…';
            const nextIsAi = Boolean(ai) || isAiProgressStatus(nextStatus);
            const parsedNext = parseProgressStatus(nextStatus);
            if (parsedNext.runId && parsedNext.seq) {
                const last = lastProgressRef.current;
                if (last.runId === parsedNext.runId && parsedNext.seq < last.seq) return;
                lastProgressRef.current = { runId: parsedNext.runId, seq: parsedNext.seq };
            }
            setStatus(nextStatus);
            setAI(nextIsAi);
            if (nextIsAi && aiStartedAt === null) {
                setAiStartedAt(Date.now());
                setAiElapsedMs(0);
            } else if (!nextIsAi) {
                setAiStartedAt(null);
                setAiElapsedMs(0);
            }
        }
    );

    useApiListener(
        Event.closeProgressModal,
        () => {
            setIsOpen(false);
            lastProgressRef.current = { runId: '', seq: 0 };
            setAiStartedAt(null);
            setAiElapsedMs(0);
        }
    );

    React.useEffect(() => {
        if (!open.current || aiStartedAt === null) return;
        const timer = window.setInterval(() => {
            setAiElapsedMs(Date.now() - aiStartedAt);
        }, 500);
        return () => window.clearInterval(timer);
    }, [aiStartedAt, open.current]);

    const parsed = parseProgressStatus(statusRef.current);
    const isAi = ai === true || parsed.mode === 'ai' || isAiProgressStatus(statusRef.current);
    const isExtraction = parsed.mode === 'extraction';
    const aiActivityMessage = getAiActivityMessage(aiElapsedMs);

    return (
        <Backdrop
            sx={{ color: '#aaa', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={open.current}
        >
            <Dialog
                open={open.current}
                PaperProps={{
                    sx: {
                        borderRadius: '14px',
                        overflow: 'hidden',
                        boxShadow: 'none',
                        maxWidth: 'none',
                    },
                }}
            >
                <Card>
                    <Eyebrow>Process Advisor</Eyebrow>
                    <HeaderRow>
                        <Title>{parsed.phase}</Title>
                        {isExtraction && <Percent>{parsed.percent}%</Percent>}
                    </HeaderRow>

                    {isAi ? (
                        <AiActivity>
                            <AiSpinner aria-label="AI generation in progress" />
                            <AiText>
                                {aiActivityMessage}
                                <AiDots aria-hidden="true"><span /><span /><span /></AiDots>
                            </AiText>
                        </AiActivity>
                    ) : isExtraction ? (
                        <BarTrack>
                            <BarFill width={parsed.percent} />
                        </BarTrack>
                    ) : (
                        <GenericActivity>
                            <AiSpinner aria-label="Working" />
                            <AiText>{parsed.detail || 'Working…'}</AiText>
                        </GenericActivity>
                    )}

                    {isExtraction && (
                        <DetailGrid>
                            <DetailBox>
                                <DetailLabel>Records scanned</DetailLabel>
                                <DetailValue>{parsed.scanned}</DetailValue>
                            </DetailBox>
                            <DetailBox>
                                <DetailLabel>With history</DetailLabel>
                                <DetailValue>{parsed.withHistory}</DetailValue>
                            </DetailBox>
                        </DetailGrid>
                    )}

                    <Footer>
                        {isAi
                            ? 'Generating AI insights can take up to 120 seconds.'
                            : parsed.detail}
                    </Footer>
                </Card>
            </Dialog>
        </Backdrop>
    );
};
